import { type ParsedUrlQuery } from 'querystring';

import type { PaymentFormData } from '../schema';

// PaymentFormData를 간단한 query parameter로 매핑
export const mapFormDataToQuery = (data: Partial<PaymentFormData>) => {
  return {
    plan: data.selectedPlan,
    org: data.organizationName,
    email: data.referenceEmail,
    months: data.selectedMonth,
    users: data.maxUserCount,
    start: data.startDate,
    skip: data.skipOrganizationName ? 'true' : 'false',
  };
};

// 유효성 검사 및 변환 함수들
const validateAndTransformNumber = (
  value: string,
  max?: number,
): string | undefined => {
  const trimmedValue = value.trim();
  const numValue = Number(trimmedValue);

  if (isNaN(numValue) || numValue <= 0) {
    return undefined;
  }

  if (max && numValue > max) {
    return undefined;
  }

  return trimmedValue;
};

const validateAndTransformDate = (value: string): string | undefined => {
  const trimmedValue = value.trim();
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

  if (!dateRegex.test(trimmedValue)) {
    return undefined;
  }

  // 실제 유효한 날짜인지 확인
  const date = new Date(trimmedValue);
  const isValidDate = date.toISOString().startsWith(trimmedValue);

  // 오늘보다 이전 날짜면 undefined 반환
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date < today) {
    return undefined;
  }

  return isValidDate ? trimmedValue : undefined;
};

// 간단한 query parameter를 PaymentFormData로 변환
// 쿼리 스트링에 정확히 값이 문자열로 있고 유효성 검사를 통과해야 해당 리턴값을 반환
export const mapQueryToFormData = (
  query: ParsedUrlQuery,
): Partial<PaymentFormData> => {
  const queryMappings: Array<{
    queryKey: string;
    formKey: keyof PaymentFormData;
    validate?: (value: string) => any;
  }> = [
    { queryKey: 'plan', formKey: 'selectedPlan' },
    { queryKey: 'org', formKey: 'organizationName' },
    { queryKey: 'email', formKey: 'referenceEmail' },
    {
      queryKey: 'months',
      formKey: 'selectedMonth',
      validate: value => validateAndTransformNumber(value, 60),
    },
    {
      queryKey: 'users',
      formKey: 'maxUserCount',
      validate: value => validateAndTransformNumber(value, 100),
    },
    {
      queryKey: 'start',
      formKey: 'startDate',
      validate: validateAndTransformDate,
    },
    {
      queryKey: 'skip',
      formKey: 'skipOrganizationName',
      validate: v => v === 'true',
    },
  ];

  return queryMappings.reduce<Partial<PaymentFormData>>(
    (result, { queryKey, formKey, validate }) => {
      const queryValue = query[queryKey];

      if (typeof queryValue === 'string' && queryValue.trim() !== '') {
        const validatedValue = validate ? validate(queryValue) : queryValue;

        // undefined가 반환된 경우 유효성 검사 실패로 간주하여 추가하지 않음
        if (validatedValue !== undefined) {
          result[formKey] = validatedValue;
        }
      }

      return result;
    },
    {},
  );
};

// URLSearchParams를 생성하여 쿼리 스트링을 만드는 유틸 함수
export const createSearchParams = (
  formData: Partial<PaymentFormData>,
): URLSearchParams => {
  const queryParams = mapFormDataToQuery(formData);
  const searchParams = new URLSearchParams();

  // formData에서 온 파라미터들 추가 (이미 검증된 데이터)
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value != null) {
      searchParams.set(key, String(value));
    }
  });

  return searchParams;
};
